import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPatch, billingGet, billingPost, billingGetWithTotal } from "~/shared/api/client";
import { raParams, type RASort, type RARange, type RAFilter } from "~/shared/api/ra-params";
import { retryTransient } from "~/shared/api/retry-transient";
import { createListQuery, createMutationHook } from "~/shared/api/query-factories";
import type {
    UnifiedPaymentDto,
    UnifiedPaymentTotalsDto,
    BalanceTransactionDto,
    BalanceTransactionTotalsDto,
    GenerationJobAdminDto,
} from "~/shared/types/billing";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const adminKeys = {
    all: ["admin"] as const,
    access: ["admin", "access"] as const,
    admins: ["admin", "admins"] as const,
    users: ["admin", "users"] as const,
    workspaces: ["admin", "workspaces"] as const,
    projects: ["admin", "projects"] as const,
    scenes: ["admin", "scenes"] as const,
    chats: ["admin", "chats"] as const,
    payments: ["admin", "payments"] as const,
    transactions: ["admin", "transactions"] as const,
    generations: ["admin", "generations"] as const,
};

// ---------------------------------------------------------------------------
// Access
// ---------------------------------------------------------------------------

export function useIsAdmin() {
    return useQuery({
        queryKey: adminKeys.access,
        queryFn: async () => {
            const result = await apiGet<{ isAdmin: boolean }>("/v2/admin/access/is-admin");
            return result.isAdmin;
        },
    });
}

// ---------------------------------------------------------------------------
// Users (with enrichment)
// ---------------------------------------------------------------------------

export const useAdminUsers = createListQuery<Record<string, unknown>>({
    baseKey: adminKeys.users,
    path: "/v2/admin/users",
    fetcher: async (path, params) => {
        const raw = await apiGet<{ users: Array<Record<string, unknown>>; total: number }>(path, params);
        const users = raw.users ?? [];
        return { data: users, total: raw.total ?? users.length };
    },
});

export const useUpdateUserStatus = createMutationHook<
    unknown[],
    { userId: string; status?: string; isSuperAdmin?: boolean }
>({
    mutationFn: (input) => {
        const promises: Promise<unknown>[] = [];
        if (input.status !== undefined) {
            promises.push(apiPatch(`/v2/users/${input.userId}/status`, { status: input.status }));
        }
        if (input.isSuperAdmin !== undefined) {
            promises.push(
                apiPatch(`/v2/users/${input.userId}/super-admin`, { isSuperAdmin: input.isSuperAdmin }),
            );
        }
        return Promise.all(promises);
    },
    invalidate: [adminKeys.users],
});

// ---------------------------------------------------------------------------
// Workspaces (with enrichment)
// ---------------------------------------------------------------------------

export const useAdminWorkspaces = createListQuery<Record<string, unknown>>({
    baseKey: adminKeys.workspaces,
    path: "/v2/admin/workspaces",
    fetcher: async (path, params) => {
        const raw = await apiGet<{ workspaces: Array<Record<string, unknown>>; total: number }>(path, params);
        const workspaces = raw.workspaces ?? [];
        return { data: workspaces, total: raw.total ?? workspaces.length };
    },
});

export const useUpdateWorkspaceStatus = createMutationHook<unknown, { workspaceId: string; status: string }>({
    mutationFn: (input) => apiPatch(`/v2/workspaces/${input.workspaceId}/status`, { status: input.status }),
    invalidate: [adminKeys.workspaces],
});

export const useIncWorkspaceBalance = createMutationHook<
    unknown,
    { workspaceId: string; amount: number; description: string; campaign?: string }
>({
    mutationFn: (input) => billingPost("/billing/balances/increment", input),
    invalidate: [adminKeys.workspaces],
});

// ---------------------------------------------------------------------------
// Projects (with enrichment)
// ---------------------------------------------------------------------------

export const useAdminProjects = createListQuery<Record<string, unknown>>({
    baseKey: adminKeys.projects,
    path: "/v2/admin/projects",
    fetcher: async (path, params) => {
        const raw = await apiGet<{ projects: Array<Record<string, unknown>>; total: number }>(path, params);
        const projects = raw.projects ?? [];
        return { data: projects, total: raw.total ?? projects.length };
    },
});

export const useUpdateProjectStatus = createMutationHook<unknown, { projectId: string; status: string }>({
    mutationFn: (input) => apiPatch(`/v2/projects/${input.projectId}/status`, { status: input.status }),
    invalidate: [adminKeys.projects],
});

// ---------------------------------------------------------------------------
// Scenes (with enrichment)
// ---------------------------------------------------------------------------

export const useAdminScenes = createListQuery<Record<string, unknown>>({
    baseKey: adminKeys.scenes,
    path: "/v2/admin/scenes",
    fetcher: async (path, params) => {
        const raw = await apiGet<{ scenes: Array<Record<string, unknown>>; total: number }>(path, params);
        const scenes = raw.scenes ?? [];
        return { data: scenes, total: raw.total ?? scenes.length };
    },
});

export const useUpdateSceneStatus = createMutationHook<unknown, { sceneId: string; status: string }>({
    mutationFn: (input) => apiPatch(`/v2/scenes/${input.sceneId}/status`, { status: input.status }),
    invalidate: [adminKeys.scenes],
});

// ---------------------------------------------------------------------------
// Chats (read-only registry)
// ---------------------------------------------------------------------------

export const useAdminChats = createListQuery<Record<string, unknown>>({
    baseKey: adminKeys.chats,
    path: "/v2/admin/chats",
    fetcher: async (path, params) => {
        const raw = await apiGet<{ chats: Array<Record<string, unknown>>; total: number }>(path, params);
        const chats = raw.chats ?? [];
        return { data: chats, total: raw.total ?? chats.length };
    },
});

// ---------------------------------------------------------------------------
// Admin Billing
// ---------------------------------------------------------------------------

export const useAdminPayments = createListQuery<UnifiedPaymentDto>({
    baseKey: adminKeys.payments,
    path: "/billing/admin/payments",
    fetcher: (path, params) => billingGetWithTotal<UnifiedPaymentDto[]>(path, params),
});

export function useAdminPaymentTotals(filter?: RAFilter) {
    return useQuery({
        queryKey: [...adminKeys.payments, "totals", filter],
        queryFn: () =>
            billingGet<UnifiedPaymentTotalsDto>(
                "/billing/admin/payments/totals",
                filter ? { filter: JSON.stringify(filter) } : undefined,
            ),
    });
}

export const useAdminTransactions = createListQuery<BalanceTransactionDto>({
    baseKey: adminKeys.transactions,
    path: "/billing/admin/transactions",
    fetcher: (path, params) => billingGetWithTotal<BalanceTransactionDto[]>(path, params),
});

export function useAdminTransactionTotals(filter?: RAFilter) {
    return useQuery({
        queryKey: [...adminKeys.transactions, "totals", filter],
        queryFn: () =>
            billingGet<BalanceTransactionTotalsDto>(
                "/billing/admin/transactions/totals",
                filter ? { filter: JSON.stringify(filter) } : undefined,
            ),
    });
}

export function useAdminTransactionModels(workspaceId?: string) {
    return useQuery({
        queryKey: [...adminKeys.transactions, "models", workspaceId],
        queryFn: () =>
            billingGet<string[]>(
                "/billing/admin/transactions/models",
                workspaceId ? { workspaceId } : undefined,
            ),
    });
}

export function useAdminTransactionProviders(workspaceId?: string) {
    return useQuery({
        queryKey: [...adminKeys.transactions, "providers", workspaceId],
        queryFn: () =>
            billingGet<string[]>(
                "/billing/admin/transactions/providers",
                workspaceId ? { workspaceId } : undefined,
            ),
    });
}

export const useReverseBonus = createMutationHook<unknown, { transactionId: string; reason: string }>({
    mutationFn: (input) => billingPost("/billing/admin/reverse-bonus", input),
    invalidate: [adminKeys.all],
});

// ---------------------------------------------------------------------------
// Generations
// ---------------------------------------------------------------------------

export function useAdminGenerations(sort?: RASort, range?: RARange, filter?: RAFilter) {
    return useQuery({
        queryKey: [...adminKeys.generations, sort, range, filter],
        queryFn: async () => {
            // Workspace/project/scene/user names are resolved server-side by the
            // admin generations endpoint (see AdminJobsService.list_admin_jobs),
            // so no per-row client-side name resolution is needed here.
            const result = await billingGetWithTotal<GenerationJobAdminDto[]>(
                "/generations/admin/list",
                raParams(sort, range, filter),
            );
            return { data: result.data ?? [], total: result.total };
        },
    });
}

export function useAdminGenerationModels() {
    return useQuery({
        queryKey: [...adminKeys.generations, "models"],
        queryFn: () => billingGet<string[]>("/generations/admin/models"),
    });
}

export function useAdminGenerationProviders() {
    return useQuery({
        queryKey: [...adminKeys.generations, "providers"],
        queryFn: () => billingGet<string[]>("/generations/admin/providers"),
    });
}

// ---------------------------------------------------------------------------
// Workspace name resolution (no batch endpoint — individual fetches)
// ---------------------------------------------------------------------------

export function useResolveWorkspaceNames(workspaceIds: string[]) {
    return useQuery({
        queryKey: ["admin", "workspaceNames", workspaceIds],
        queryFn: () => resolveWorkspaceNames(workspaceIds),
        enabled: workspaceIds.length > 0,
    });
}

interface WorkspaceInfo {
    name: string;
    ownerEmail: string | null;
}

/**
 * Название и почта владельца по каждому пространству — для подписи строки в админских
 * таблицах.
 *
 * Запрос на пространство остаётся: админский листинг отдаёт почту владельца, а batch-роут
 * пространств — только название, и без почты подпись теряет смысл. Что изменилось:
 * временный сбой больше не превращается молча в строку без имени — запрос повторяется,
 * а исчерпав повторы, оставляет короткий префикс идентификатора, как это делает бэкенд
 * для неизвестных id. Отличать «пространства нет» от «сервер не ответил» по пустому месту
 * в таблице было невозможно.
 */
async function resolveWorkspaceInfo(workspaceIds: string[]): Promise<Record<string, WorkspaceInfo>> {
    const info: Record<string, WorkspaceInfo> = {};
    await Promise.all(
        workspaceIds.map(async (id) => {
            try {
                const result = await retryTransient(() =>
                    apiGet<{
                        workspaces: Array<Record<string, unknown>>;
                        total: number;
                    }>("/v2/admin/workspaces", raParams(undefined, [0, 0], { workspaceId: id })),
                );
                const workspace = result.workspaces?.[0];
                if (workspace) {
                    info[id] = {
                        name: (workspace.name as string) ?? id.slice(0, 8),
                        ownerEmail: (workspace.ownerEmail as string) ?? null,
                    };
                }
            } catch {
                // Повторы исчерпаны либо отказ по существу: строка получает короткий
                // префикс id вместо названия — читаемо и не выдаёт сбой за пустоту.
                info[id] = { name: id.slice(0, 8), ownerEmail: null };
            }
        }),
    );
    return info;
}

async function resolveWorkspaceNames(workspaceIds: string[]): Promise<Record<string, string>> {
    const info = await resolveWorkspaceInfo(workspaceIds);
    const names: Record<string, string> = {};
    for (const [id, w] of Object.entries(info)) names[id] = w.name;
    return names;
}
